/**
 * Secure storage utility for managing sensitive data.
 *
 * Token storage:
 *   - Default: sessionStorage (cleared on tab close) — safest, but re-login
 *     is required after each browser restart.
 *   - "Remember me" opt-in: localStorage (persistent across tabs/reboots).
 *     Enabled explicitly by passing `persistent: true` to setToken().
 *   - Fallback: in-memory when storage is unavailable.
 * All token values are base64-encoded to prevent casual devtools inspection.
 *
 * Non-sensitive data (UI preferences, tutorial state): localStorage.
 */

const TOKEN_KEY = 'exrsim-token';
const REMEMBER_KEY = 'exrsim-remember-me';

// In-memory fallback for environments where storage is unavailable
let memoryStore = {};

// Simple encode/decode to prevent casual token inspection in devtools
const encode = (value) => {
  try { return btoa(encodeURIComponent(value)); } catch { return value; }
};
const decode = (value) => {
  try { return decodeURIComponent(atob(value)); } catch { return value; }
};

// Sensitive key patterns that must never go through the preference API
const SENSITIVE_KEY_PATTERN = /token|auth|password|secret|jwt|credential/i;

const secureStorage = {
  /**
   * Get the auth token. Checks (in order):
   *   1. sessionStorage (current tab, encoded)
   *   2. localStorage — but only if "remember me" was opted-in (encoded)
   *   3. Legacy localStorage token (unencoded) for one-time migration
   *   4. In-memory fallback
   */
  getToken: () => {
    try {
      const sessionVal = sessionStorage.getItem(TOKEN_KEY);
      if (sessionVal) return decode(sessionVal);

      // "Remember me" persistent token in localStorage (encoded)
      const rememberEnabled = localStorage.getItem(REMEMBER_KEY) === '1';
      if (rememberEnabled) {
        const persistVal = localStorage.getItem(TOKEN_KEY);
        if (persistVal) {
          const decoded = decode(persistVal);
          // Rehydrate sessionStorage so subsequent same-tab reads are fast
          try { sessionStorage.setItem(TOKEN_KEY, persistVal); } catch { /* noop */ }
          return decoded;
        }
      }

      // Legacy migration: check unencoded localStorage, then remove it
      const legacyVal = localStorage.getItem(TOKEN_KEY);
      if (legacyVal && !rememberEnabled) {
        // Old-style plaintext token — migrate to sessionStorage and drop from LS
        try { sessionStorage.setItem(TOKEN_KEY, encode(legacyVal)); } catch { /* noop */ }
        try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
        return legacyVal;
      }
      return memoryStore[TOKEN_KEY] || null;
    } catch {
      return memoryStore[TOKEN_KEY] || null;
    }
  },

  /**
   * Store the auth token (encoded).
   * @param {string} token - JWT
   * @param {boolean} persistent - When true, also stores in localStorage so the
   *   session survives tab close and browser restart ("Remember me"). When false
   *   (default), only sessionStorage is used and the token dies with the tab.
   */
  setToken: (token, persistent = false) => {
    try {
      const encoded = encode(token);
      sessionStorage.setItem(TOKEN_KEY, encoded);
      memoryStore[TOKEN_KEY] = token;
      if (persistent) {
        localStorage.setItem(TOKEN_KEY, encoded);
        localStorage.setItem(REMEMBER_KEY, '1');
      } else {
        // Not remembering — make sure no stale persistent copy lingers
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      memoryStore[TOKEN_KEY] = token;
    }
  },

  /**
   * True if the current session was started with "Remember me".
   */
  isRemembered: () => {
    try { return localStorage.getItem(REMEMBER_KEY) === '1'; } catch { return false; }
  },

  /**
   * Remove the auth token from all stores.
   */
  removeToken: () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REMEMBER_KEY);
      delete memoryStore[TOKEN_KEY];
    } catch {
      delete memoryStore[TOKEN_KEY];
    }
  },

  /**
   * Get a non-sensitive preference value from localStorage.
   */
  getPreference: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return memoryStore[key] || null;
    }
  },

  /**
   * Set a non-sensitive preference value in localStorage.
   * Sensitive keys (token, auth, password, etc.) are rejected to prevent accidental misuse.
   */
  setPreference: (key, value) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      console.error(
        `[secureStorage] Refusing to store sensitive key "${key}" in localStorage. ` +
        `Use setToken() for auth tokens; sensitive data must not persist in localStorage.`
      );
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      memoryStore[key] = value;
    }
  },

  /**
   * Remove a preference from localStorage.
   */
  removePreference: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      delete memoryStore[key];
    }
  },
};

export default secureStorage;
